import { useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { bibleState } from '@/state/bible-state';
import { formatReference } from '@skjv/common';
import type { Note } from '@skjv/common';
import { NoteIcon, TrashIcon } from '@components/icons';
import { EmptyState } from '@components/common';

/**
 * Notes tab content — verse notes in canonical order.
 */
export const NotesContent = observer(function NotesContent() {
  const notes = [...bibleState.notes].sort(
    (a, b) =>
      a.testamentIndex - b.testamentIndex ||
      a.bookIndex - b.bookIndex ||
      a.chapterIndex - b.chapterIndex ||
      a.verseIndex - b.verseIndex,
  );

  const handleLoad = useCallback((note: Note) => {
    bibleState.setCurrentLocation(note);
    // Keep panel open so user can browse multiple notes
  }, []);

  const handleDelete = useCallback((note: Note) => {
    bibleState.showConfirmDialog({
      title: 'Delete Note',
      message: 'Delete this note?',
      confirmText: 'Delete',
      variant: 'danger',
      onConfirm: () => bibleState.removeNoteAt(note),
    });
  }, []);

  if (notes.length === 0) {
    return (
      <EmptyState
        icon={<NoteIcon className="h-10 w-10" />}
        title="No notes yet"
        hint="Tap a verse number and choose Create Note to write one"
      />
    );
  }

  return (
    <>
      {notes.map((note) => (
        <div
          key={`${note.testamentIndex}-${note.bookIndex}-${note.chapterIndex}-${note.verseIndex}`}
          className="bookmark-row"
        >
          <div className="bookmark-row-header">
            <div>
              <div className="bookmark-name">{formatReference(note)}</div>
              <div className="mt-1 whitespace-pre-wrap text-sm text-stone-600 dark:text-stone-300">
                {note.noteText}
              </div>
            </div>
          </div>
          <div className="bookmark-actions">
            <button
              onClick={() => handleDelete(note)}
              className="bookmark-action-btn hover:text-red-600 dark:hover:text-red-400"
              title="Delete"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
            <div className="flex-1" />
            <button onClick={() => handleLoad(note)} className="bookmark-load-btn">
              Load
            </button>
          </div>
        </div>
      ))}
    </>
  );
});
